import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import type { PartnerCapabilities } from '@/domain/types';
import { dashboardPathFor } from '@/domain/navigation';
import { MainLayout } from '@/components/MainLayout';

// Lazy load pages to reduce bundle size
const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPasswordPage'));
const FirstLoginPage = React.lazy(() => import('@/pages/auth/FirstLoginPage'));
const ProfilePage = React.lazy(() => import('@/pages/shared/ProfilePage'));

// Glaukos rep pages
const RepDashboard = React.lazy(() => import('@/pages/shared/RepDashboard'));
const ReferralListPage = React.lazy(() => import('@/pages/shared/ReferralListPage'));
const ReferralDetailPage = React.lazy(() => import('@/pages/shared/ReferralDetailPage'));
const CreateReferralPage = React.lazy(() => import('@/pages/shared/CreateReferralPage'));

// Topcon rep pages
const TrialListPage = React.lazy(() => import('@/pages/shared/TrialListPage'));
const CreateTrialPage = React.lazy(() => import('@/pages/shared/CreateTrialPage'));
const TrialDetailPage = React.lazy(() => import('@/pages/shared/TrialDetailPage'));

// Admin pages
const AdminDashboard = React.lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminReferralListPage = React.lazy(() => import('@/pages/admin/AdminReferralListPage'));
const CommissionConfigPage = React.lazy(() => import('@/pages/shared/CommissionConfigPage'));
const ConfigurationsPage = React.lazy(() => import('@/pages/admin/ConfigurationsPage'));
const PartnerListPage = React.lazy(() => import('@/pages/shared/PartnerListPage'));
const PartnerFormPage = React.lazy(() => import('@/pages/shared/PartnerFormPage'));
const CreateSalesRepPage = React.lazy(() => import('@/pages/admin/CreateSalesRepPage'));
const AdminSalesRepListPage = React.lazy(() => import('@/pages/admin/AdminSalesRepListPage'));

// Shared pages
const NotificationCentrePage = React.lazy(() => import('@/pages/shared/NotificationCentrePage'));

// Protected route wrapper
interface ProtectedRouteProps {
  element: React.ReactElement;
  requiredRole?: 'SALES_REP' | 'RADIUS_ADMIN';
  /**
   * Route is reachable only when the rep's partner has this setting enabled.
   * Admins are exempt — they are gated by `requiredRole` instead.
   *
   * Capabilities come from the auth store, which useCurrentUserSync keeps in
   * step with the server, so revoking a setting locks the route on the next
   * refetch without a re-login. The server re-checks regardless; this only
   * avoids showing a rep a screen that would refuse them.
   */
  requiredCapability?: keyof Omit<PartnerCapabilities, 'commissionAmount'>;
}

function ProtectedRoute({
  element,
  requiredRole,
  requiredCapability,
}: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/login" />;
  }

  if (requiredCapability && user.role !== 'RADIUS_ADMIN'
    && !user.partner?.capabilities?.[requiredCapability]) {
    // Signed in, just not permitted here — send them home rather than to login.
    return <Navigate to="/" replace />;
  }

  return <MainLayout>{element}</MainLayout>;
}

/** Keeps old partner-prefixed detail links working after the path move. */
function LegacyDetailRedirect({ to }: { to: 'referrals' | 'trials' }) {
  const { id } = useParams();
  return <Navigate to={`/${to}/${id}`} replace />;
}

export function AppRouter() {
  const { isAuthenticated, user } = useAuthStore();

  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* Landing page for the ?token= link in the reset email */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/first-login" element={<FirstLoginPage />} />

        {/* One dashboard for every partner; sections chosen by what the partner
            is allowed to do. The old partner-named paths redirect. */}
        <Route
          path="/dashboard"
          element={<ProtectedRoute element={<RepDashboard />} requiredRole="SALES_REP" />}
        />
        <Route path="/glaukos/dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/topcon/dashboard" element={<Navigate to="/dashboard" replace />} />

        {/* Referral routes — open to any rep whose partner is allowed, rather
            than to one named partner. Creating is gated separately from
            viewing, so revoking creation leaves a rep able to read what they
            already filed. */}
        <Route
          path="/referrals"
          element={
            <ProtectedRoute
              element={<ReferralListPage />}
              requiredRole="SALES_REP"
              requiredCapability="canViewReferrals"
            />
          }
        />
        <Route
          path="/referrals/new"
          element={
            <ProtectedRoute
              element={<CreateReferralPage />}
              requiredRole="SALES_REP"
              requiredCapability="canCreateReferrals"
            />
          }
        />

        {/* Trial routes — same treatment. */}
        <Route
          path="/trials"
          element={
            <ProtectedRoute
              element={<TrialListPage />}
              requiredRole="SALES_REP"
              requiredCapability="canViewTrials"
            />
          }
        />

        <Route
          path="/trials/new"
          element={
            <ProtectedRoute
              element={<CreateTrialPage />}
              requiredRole="SALES_REP"
              requiredCapability="canCreateTrials"
            />
          }
        />

        {/* Legacy partner-prefixed URLs: bookmarks, older notification links. */}
        <Route path="/glaukos/referrals" element={<Navigate to="/referrals" replace />} />
        <Route path="/glaukos/referrals/new" element={<Navigate to="/referrals/new" replace />} />
        <Route path="/glaukos/referrals/:id" element={<LegacyDetailRedirect to="referrals" />} />
        <Route path="/topcon/trials" element={<Navigate to="/trials" replace />} />
        <Route path="/topcon/trials/:id" element={<LegacyDetailRedirect to="trials" />} />

        {/* Admin routes */}
        <Route
          path="/admin/dashboard"
          element={<ProtectedRoute element={<AdminDashboard />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/referrals"
          element={<ProtectedRoute element={<AdminReferralListPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/trials"
          element={<ProtectedRoute element={<TrialListPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/commission"
          element={<ProtectedRoute element={<CommissionConfigPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/configurations"
          element={<ProtectedRoute element={<ConfigurationsPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/partners"
          element={<ProtectedRoute element={<PartnerListPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/partners/new"
          element={<ProtectedRoute element={<PartnerFormPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/partners/:id"
          element={<ProtectedRoute element={<PartnerFormPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/sales-reps"
          element={<ProtectedRoute element={<AdminSalesRepListPage />} requiredRole="RADIUS_ADMIN" />}
        />
        <Route
          path="/admin/sales-reps/new"
          element={<ProtectedRoute element={<CreateSalesRepPage />} requiredRole="RADIUS_ADMIN" />}
        />

        {/* Shared routes */}
        <Route path="/trials/:id" element={<ProtectedRoute element={<TrialDetailPage />} />} />
        <Route path="/referrals/:id" element={<ProtectedRoute element={<ReferralDetailPage />} />} />
        <Route path="/notifications" element={<ProtectedRoute element={<NotificationCentrePage />} />} />
        <Route path="/profile" element={<ProtectedRoute element={<ProfilePage />} />} />

        {/* Default redirect */}
        <Route
          path="/"
          element={
            isAuthenticated && user
              ? user.role === 'RADIUS_ADMIN'
                ? <Navigate to="/admin/dashboard" />
                : <Navigate to={dashboardPathFor()} />
              : <Navigate to="/login" />
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
